import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import {
  Columns3,
  FilterX,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { DesignatedVendor } from "@/features/partners/api";
import AuthorizedButton from "@/components/common/AuthorizedButton";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import PageHeader from "@/components/common/PageHeader";
import Paginator from "@/components/common/Paginator";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsDialog from "@/components/table/ColumnSettingsDialog";
import DataTable from "@/components/table/DataTable";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { previewQuotationSetProduct } from "@/features/setProduct/api";
import SetProductDialog from "@/features/setProduct/SetProductDialog";
import { showError, showSuccess } from "@/lib/toast";
import {
  deleteQuotations,
  listQuotations,
  QUOTATION_STATUS_LABEL,
  setQuotationSetProduct,
  updateQuotation,
  type Quotation,
  type QuotationInput,
  type QuotationStatus,
} from "./api";
import EdiMenuButton from "./EdiMenuButton";
import QuotationBulkDeleteDialog from "./QuotationBulkDeleteDialog";
import QuotationCreateDialog from "./QuotationCreateDialog";
import QuotationImportDialog from "./QuotationImportDialog";
import {
  buildQuotationColumns,
  quotationColumnIds,
  quotationDefaultHiddenColumnIds,
  quotationPinnedColumnIds,
  type QuotationField,
} from "./columns";
import { QUOTATIONS_PAGE_SIZE, useQuotationListParams } from "./useQuotationListParams";

/** 状態セレクトの選択肢（先頭は絞り込み解除）。 */
const STATUS_OPTIONS = [
  { key: "all", label: "すべて" },
  ...Object.entries(QUOTATION_STATUS_LABEL).map(([key, label]) => ({ key, label })),
];

/**
 * 見積一覧。検索条件は URL、表示列の設定は localStorage に持つ。
 *
 * 画面構成は ngtg_msc の受注管理（projects_view）に合わせている。パンくず・
 * 操作ボタン・テーブルを 1 枚のカードに収め、ページャはカードの外（下端）に置く。
 */
export default function QuotationList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { params, update, reset: resetParams } = useQuotationListParams();
  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings(
    "quotations",
    quotationColumnIds,
    quotationPinnedColumnIds,
    quotationDefaultHiddenColumnIds,
  );
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();
  const { user } = useAuth();
  const editable = canWrite(user, "transactional");
  const [creating, setCreating] = useState(false);
  /** まとめて削除するために選んだ見積の id。 */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  /** セット品の切り替えの確認ダイアログ（押した見積と切り替え先）。 */
  const [togglingSetProduct, setTogglingSetProduct] = useState<{
    quotation: Quotation;
    to: boolean;
  } | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // EDI メニューで選ばれた取り込み先。null なら取り込みダイアログは閉じている。
  const [importing, setImporting] = useState<{
    vendor: DesignatedVendor;
    label: string;
  } | null>(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  /**
   * いま編集している 1 項目。
   *
   * ダイアログは一覧が 1 つだけ持つ。セルごとに Modal を抱えると、行数 × 列数
   * ぶんの Modal が描かれる（案件一覧・取引先一覧と同じ作り）。
   */
  const [editing, setEditing] = useState<{
    quotation: Quotation;
    field: QuotationField;
  } | null>(null);

  const query = useQuery({
    queryKey: ["quotations", params],
    queryFn: () =>
      listQuotations({
        keyword: params.keyword || undefined,
        status: params.status ?? undefined,
        sortKey: params.sortKey,
        ascending: params.ascending,
        limit: QUOTATIONS_PAGE_SIZE,
        offset: (params.page - 1) * QUOTATIONS_PAGE_SIZE,
      }),
  });

  const rows = query.data?.items ?? [];

  /**
   * 選ばれている見積。**いま画面に出ている行から引き直す**ので、消えた見積や
   * ページを移って見えなくなった見積の id が残っていても無視される。
   */
  const selected = rows.filter((q) => selectedIds.has(q.quotation_id));
  const allSelected = rows.length > 0 && selected.length === rows.length;

  // ページや検索条件を変えたら選択を捨てる。見えていない行を選んだまま削除
  // できると、何を消すのかが画面から読めなくなる。
  const paramsKey = JSON.stringify(params);
  useEffect(() => setSelectedIds(new Set()), [paramsKey]);

  const toggleSelect = (quotation: Quotation) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(quotation.quotation_id)) next.delete(quotation.quotation_id);
      else next.add(quotation.quotation_id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((q) => q.quotation_id)));

  const removeMany = useMutation({
    mutationFn: (ids: number[]) => deleteQuotations(ids),
    onSuccess: async (_data, ids) => {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      setSelectedIds(new Set());
      setBulkDeleting(false);
      showSuccess(`見積 ${ids.length} 件を削除しました。`);
    },
    // 失敗の理由（案件に紐付いた見積など）はダイアログの中に出す。閉じて
    // しまうと、どれを選択から外せばよいか分からなくなる。
  });

  /**
   * セット品の印を切り替える。**行き先は紙の行があるかで変わる**。
   *
   * 購番の行が付いている見積は、切り替えると**見積と案件の明細の形まで変わる**
   * （畳み込み・解除）ので確認ダイアログを通す。紙の行が無い見積は印とセット数
   * しか動かないので、押した場所でそのまま終わらせる。外すとセット数も消える
   * ため、消えたことはトーストで知らせる。
   */
  const toggleSetProduct = async (quotation: Quotation) => {
    const to = !quotation.is_set_product;
    if (quotation.client_order_numbers.length > 0) {
      setTogglingSetProduct({ quotation, to });
      return;
    }
    const hadCount = quotation.set_count != null;
    try {
      await updateQuotation(quotation.quotation_id, { is_set_product: to });
    } catch (e) {
      showError(errorMessage(e, "セット品の切り替えに失敗しました。"));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["quotations"] });
    await queryClient.invalidateQueries({
      queryKey: ["quotation", quotation.quotation_id],
    });
    if (to) {
      showSuccess(`${quotation.quotation_number} をセット品にしました。`);
    } else {
      showSuccess(
        hadCount
          ? `${quotation.quotation_number} のセット品を解除しました（セット数も消えました）。`
          : `${quotation.quotation_number} のセット品を解除しました。`,
      );
    }
  };

  const columns = useMemo(
    () =>
      buildQuotationColumns({
        canWrite: editable,
        selectedIds,
        onToggleSelect: toggleSelect,
        onEditField: (quotation, field) => setEditing({ quotation, field }),
        onToggleSetProduct: toggleSetProduct,
      }),
    // toggleSelect / toggleSetProduct は毎回作り直されるが、中身は選択
    // （selectedIds）と書き込み権限だけに依存する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable, selectedIds],
  );

  /** 1 項目だけ保存する。エラー文言を返すとダイアログは開いたまま。 */
  const save = async (
    quotation: Quotation,
    input: QuotationInput,
  ): Promise<string | null> => {
    try {
      await updateQuotation(quotation.quotation_id, input);
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      // 見積詳細を開いている最中に一覧から直すこともある。
      await queryClient.invalidateQueries({
        queryKey: ["quotation", quotation.quotation_id],
      });
      return null;
    } catch (e) {
      // 入力し直せば解決する失敗（失注理由なしの失注など）はダイアログ内に返す。
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const editTarget: SingleFieldTarget | null = editing
    ? {
        label: editing.field.label,
        value: editing.field.value(editing.quotation),
        kind: editing.field.kind ?? "text",
        required: editing.field.required,
        description: editing.field.description,
        options: editing.field.options,
      }
    : null;

  // 並び替えは API 側で行う（manualSorting）。列定義の meta.sortKey を使う。
  const sorting: SortingState = [{ id: params.sortKey, desc: !params.ascending }];

  const table = useReactTable<Quotation>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.quotation_id),
    columnResizeMode: "onChange",
    manualSorting: true,
    manualPagination: true,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) return;
      // 並び替えキーを持たない列（明細数・金額など）は API が対応していない。
      const sortKey = table.getColumn(first.id)?.columnDef.meta?.sortKey;
      if (!sortKey) return;
      update({ sortKey, ascending: !first.desc });
    },
    // meta.sortKey を持つ列だけ並び替え可能にする。
    enableSorting: true,
    defaultColumn: {
      enableSorting: false,
      minSize: 60,
      maxSize: 600,
    },
  });

  // 列ごとの並び替え可否は meta.sortKey の有無で決まる。
  for (const column of table.getAllLeafColumns()) {
    column.columnDef.enableSorting = Boolean(column.columnDef.meta?.sortKey);
  }

  const total = query.data?.pagination.total ?? 0;

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title="見積管理"
            actions={
              <>
                <Input
                  aria-label="見積番号・得意先名で検索"
                  placeholder="見積番号・得意先名"
                  variant="bordered"
                  radius="sm"
                  className="w-60"
                  startContent={<Search size={16} className="text-default-400" />}
                  defaultValue={params.keyword}
                  onBlur={(e) =>
                    update({ keyword: (e.target as HTMLInputElement).value.trim() })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      update({
                        keyword: (e.target as HTMLInputElement).value.trim(),
                      });
                    }
                  }}
                  isClearable
                  onClear={() => update({ keyword: "" })}
                />
                <Select
                  aria-label="状態で絞り込む"
                  variant="bordered"
                  radius="sm"
                  className="w-44"
                  disallowEmptySelection
                  selectedKeys={[params.status ?? "all"]}
                  onSelectionChange={(keys) => {
                    const value = [...keys][0] as string | undefined;
                    update({
                      status:
                        !value || value === "all" ? null : (value as QuotationStatus),
                    });
                  }}
                  // 閉じている間は ngtg_msc の「種別：<選択値>」に倣って
                  // 項目名込みで見せる（何で絞っているかが一目で分かる）。
                  renderValue={(items) => {
                    const key = items[0]?.key as string | undefined;
                    const label =
                      key && key !== "all"
                        ? QUOTATION_STATUS_LABEL[key as QuotationStatus]
                        : "すべて";
                    return `状態：${label}`;
                  }}
                  classNames={{
                    trigger: params.status ? "border-brand-500" : "",
                    value: params.status ? "font-bold text-brand-500" : "",
                  }}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                <AuthorizedButton
                  scope="transactional"
                  radius="full"
                  color="primary"
                  data-testid="quotation-create"
                  startContent={<Plus size={18} />}
                  onPress={() => setCreating(true)}
                >
                  新規見積
                </AuthorizedButton>

                <EdiMenuButton
                  onSelect={(key, label) =>
                    setImporting({ vendor: key as DesignatedVendor, label })
                  }
                />

                {/* 表示列の設定やリセットなど、常用しない操作はケバブに畳む
                  （ngtg_msc の _SettingsMenuButton と同じ）。 */}
                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      radius="full"
                      aria-label="表示設定"
                    >
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="表示設定"
                    onAction={(key) => {
                      if (key === "columns") setColumnSettingsOpen(true);
                      if (key === "reload") void query.refetch();
                      if (key === "reset_filters") resetParams();
                      if (key === "reset_columns") resetColumns();
                    }}
                  >
                    <DropdownItem key="columns" startContent={<Columns3 size={18} />}>
                      表示列の設定
                    </DropdownItem>
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    <DropdownItem
                      key="reset_filters"
                      startContent={<FilterX size={18} />}
                    >
                      検索条件リセット
                    </DropdownItem>
                    <DropdownItem
                      key="reset_columns"
                      startContent={<Columns3 size={18} />}
                    >
                      表示列リセット
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />

          {/* 選択と、選んだ見積への操作。**操作は選んでから**出す（全件に効く
              ボタンを常に置くと、押し間違えたときの被害が大きい）。
              「すべて選択」が効くのは**いま出ているページ**だけ。ページを
              またいで選べるようにすると、画面に無い見積まで消えてしまう。 */}
          {editable && (
            <div className="flex min-h-9 flex-wrap items-center gap-3 px-6 pb-2">
              <Checkbox
                size="sm"
                isSelected={allSelected}
                isIndeterminate={selected.length > 0 && !allSelected}
                isDisabled={rows.length === 0}
                onValueChange={toggleSelectAll}
                data-testid="quotation-select-all"
              >
                <span className="text-sm">このページをすべて選択</span>
              </Checkbox>
              {selected.length > 0 && (
                <>
                  <span className="text-sm font-bold text-brand-600">
                    {selected.length} 件を選択中
                  </span>
                  <AuthorizedButton
                    scope="transactional"
                    size="sm"
                    radius="full"
                    variant="light"
                    color="danger"
                    data-testid="quotation-bulk-delete"
                    startContent={<Trash2 size={16} />}
                    isLoading={removeMany.isPending}
                    onPress={() => setBulkDeleting(true)}
                  >
                    選んだ {selected.length} 件を削除
                  </AuthorizedButton>
                </>
              )}
            </div>
          )}

          <div className="min-h-0 flex-1">
            {query.isError ? (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <p className="text-default-600">
                  データの取得に失敗しました。
                  <br />
                  {(query.error as Error).message}
                </p>
                <Button color="primary" onPress={() => void query.refetch()}>
                  再読み込み
                </Button>
              </div>
            ) : query.isPending ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label="読み込み中..." />
              </div>
            ) : (
              <DataTable
                table={table}
                isLoading={query.isFetching}
                emptyMessage="見積がありません"
                scrollRef={scrollRef}
              />
            )}
          </div>
        </section>

        <Paginator
          total={total}
          pageSize={QUOTATIONS_PAGE_SIZE}
          page={params.page}
          onChange={(page) => update({ page })}
        />
      </div>

      {/* 左右端の横スクロールボタン（ngtg_msc の _ScrollEdgeButton 相当）。
          カードの外側、画面の左右余白（24px）にちょうど収まる幅にしてある。
          msc に合わせて常時表示し、その向きへスクロールできないときだけ薄くする。 */}
      <SingleFieldEditDialog
        target={editTarget}
        onClose={() => setEditing(null)}
        onSave={(value) =>
          editing
            ? save(editing.quotation, editing.field.toInput(value))
            : Promise.resolve(null)
        }
      />

      <ColumnSettingsDialog
        isOpen={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        table={table}
        onReset={resetColumns}
      />

      <QuotationCreateDialog
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={(quotation) => {
          showSuccess(`見積 ${quotation.quotation_number} を起票しました。`);
          void queryClient.invalidateQueries({ queryKey: ["quotations"] });
          // 起票したら続けて明細を入れるため、そのまま詳細へ移動する。
          navigate(`/quotations/${quotation.quotation_id}`);
        }}
      />

      {togglingSetProduct && (
        <SetProductDialog
          owner="quotation"
          number={togglingSetProduct.quotation.quotation_number}
          to={togglingSetProduct.to}
          previewKey={[
            "quotation-set-product-preview",
            togglingSetProduct.quotation.quotation_id,
            togglingSetProduct.to,
          ]}
          fetchPreview={() =>
            previewQuotationSetProduct(
              togglingSetProduct.quotation.quotation_id,
              togglingSetProduct.to,
            )
          }
          onClose={() => setTogglingSetProduct(null)}
          onConfirm={async () => {
            const { quotation, to } = togglingSetProduct;
            await setQuotationSetProduct(quotation.quotation_id, to);
            // 案件の明細も一緒に作り直すので、案件側も取り直す。
            await queryClient.invalidateQueries({ queryKey: ["quotations"] });
            await queryClient.invalidateQueries({
              queryKey: ["quotation", quotation.quotation_id],
            });
            await queryClient.invalidateQueries({ queryKey: ["projects"] });
            setTogglingSetProduct(null);
            showSuccess(
              to
                ? `${quotation.quotation_number} をセット品にしました。`
                : `${quotation.quotation_number} のセット品を解除しました。`,
            );
          }}
        />
      )}

      <QuotationBulkDeleteDialog
        isOpen={bulkDeleting}
        quotations={selected}
        onClose={() => setBulkDeleting(false)}
        onConfirm={() =>
          removeMany.mutateAsync(selected.map((q) => q.quotation_id)).then(() => {})
        }
      />

      <QuotationImportDialog
        isOpen={importing != null}
        vendor={importing?.vendor ?? null}
        formatLabel={importing?.label ?? ""}
        onClose={() => setImporting(null)}
        onImported={(quotations) => {
          const numbers = quotations.map((q) => q.quotation_number).join("、");
          showSuccess(`見積 ${numbers} を取り込みました。`);
          void queryClient.invalidateQueries({ queryKey: ["quotations"] });
          // 1 件なら内容を確かめられるよう、そのまま詳細へ移動する。分けて
          // 取り込んだときは移動先が選べないので、一覧で見比べてもらう。
          if (quotations.length === 1) {
            navigate(`/quotations/${quotations[0].quotation_id}`);
          }
        }}
      />

      <EdgeScrollButton
        side="left"
        isDisabled={!canScrollLeft}
        onPress={() => scrollBy(-1)}
      />
      <EdgeScrollButton
        side="right"
        isDisabled={!canScrollRight}
        onPress={() => scrollBy(1)}
      />
    </div>
  );
}
